import React from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import CheckIcon from '@mui/icons-material/Check';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import InsightsIcon from '@mui/icons-material/Insights';
import ShieldIcon from '@mui/icons-material/Shield';
import BadgeIcon from '@mui/icons-material/Badge';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import GavelIcon from '@mui/icons-material/Gavel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from './analytics';

const pillars = [
  {
    icon: <VolumeUpIcon sx={{ fontSize: 24 }} />,
    accent: '#ff9f43',
    title: 'The only platform that hears abuse, not just sees it',
    body: 'Emotional and verbal abuse — shouting, threats, degrading language, persistent distress — causes lasting harm and leaves no visible trace. SMCLA\'s audio AI analyzes tone, language patterns, and distress signals in real time. This is a category of harm that cameras alone are structurally blind to. We built the solution.',
  },
  {
    icon: <InsightsIcon sx={{ fontSize: 24 }} />,
    accent: '#8c52ff',
    title: 'Behavior recognized across sessions, not just incidents',
    body: 'A single rough interaction might be ambiguous. Fourteen interactions involving the same worker and the same child across three weeks is a pattern of abuse. SMCLA connects the dots automatically — flagging escalating behavior that no single-incident review would ever catch.',
  },
  {
    icon: <SmartToyIcon sx={{ fontSize: 24 }} />,
    accent: '#5ce1e6',
    title: 'AI trained specifically on childcare behavior',
    body: 'An adult picking up a child is normal. Certain physical interactions are not. Generic security AI can\'t tell the difference — it wasn\'t built for this context. SMCLA\'s models are trained on childcare-specific behavior, which means fewer false alarms and fewer missed incidents that generic systems either miss or misidentify.',
  },
  {
    icon: <ShieldIcon sx={{ fontSize: 24 }} />,
    accent: '#5ce1e6',
    title: 'Protection works in both directions',
    body: 'SMCLA protects children from staff who shouldn\'t be trusted. It also protects good staff from false accusations that would otherwise be their word against a parent\'s. Timestamped, tamper-evident clip evidence resolves disputes immediately — before a career is destroyed and before a lawsuit is filed.',
  },
  {
    icon: <CameraAltIcon sx={{ fontSize: 24 }} />,
    accent: '#5ce1e6',
    title: 'Works with the cameras you already have',
    body: 'No rip-and-replace. No proprietary hardware requirement. SMCLA connects to your existing IP cameras — Axis, Hanwha, Hikvision, Bosch, Dahua, Uniview — and adds behavioral intelligence on top. The total cost of adoption is a fraction of solutions that require you to start over.',
  },
  {
    icon: <AdminPanelSettingsIcon sx={{ fontSize: 24 }} />,
    accent: '#8c52ff',
    title: 'Role-based access designed for how daycares actually work',
    body: 'The facility owner sees everything and takes action. The reviewer classifies alerts. The floor manager sees only what\'s been shared with them. SMCLA\'s access model mirrors the org structure of real childcare operations — so the right person sees the right information, and no one sees more than they should.',
  },
  {
    icon: <GavelIcon sx={{ fontSize: 24 }} />,
    accent: '#ff9f43',
    title: 'Audit trail built for licensing boards and courts',
    body: 'Every alert — true or false — is permanently logged with a reference key, reviewer notes, timestamps, and chain-of-custody metadata. Dismissed alerts aren\'t deleted; they\'re documented. The incident record is structured for childcare licensing requirements and legal proceedings, not generic IT security logs.',
  },
  {
    icon: <BadgeIcon sx={{ fontSize: 24 }} />,
    accent: '#5ce1e6',
    title: 'Privacy-first by design',
    body: 'SMCLA does not store continuous footage. Video is analyzed and discarded — only flagged clips are retained, and only reviewers with explicit access can view them. Every access is logged. Children\'s privacy is protected, staff dignity is preserved, and your facility is insulated from the liability of sitting on a surveillance archive.',
  },
  {
    icon: <ManageSearchIcon sx={{ fontSize: 24 }} />,
    accent: '#ff9f43',
    title: 'Incident investigation with full-facility reconstruction',
    body: 'When a reviewer confirms a true incident, SMCLA creates an investigation and surfaces 24 hours of footage from every camera in the facility — automatically. You see what happened in the room, and what happened in every other room in the hours before. The complete package — clips, audit trail, chain-of-custody metadata — is ready for law enforcement, licensing boards, or legal counsel. No other platform does this.',
  },
];

const failureScenarios = [
  {
    title: 'Bright Horizons, Manhattan — 18 months of abuse despite cameras',
    body: 'The facility had cameras. Three workers abused nine children over a year and a half. Manual review missed it entirely. Behavioral AI analyzing patterns across sessions would have flagged the first incident — not the 50th.',
  },
  {
    title: 'Centennial, Colorado — 51 counts before anyone noticed',
    body: 'Brandon Vigil committed 51 documented acts of abuse against 2-year-olds. No monitoring system caught a pattern. AI trained on childcare behavior would have detected the repeated degrading behavior after the first few incidents.',
  },
  {
    title: 'Blanding, Utah — footage existed, but the damage was already done',
    body: 'Security footage confirmed the abuse — after the fact. The child had already been harmed. A 60-second real-time alert is the difference between stopping it and documenting it.',
  },
];

export default function ComparePage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const handleBookDemo = () => { trackEvent('cta_click', 'Book a Demo', pathname); navigate('/contact'); };
  const handleGetQuote = () => { trackEvent('cta_click', 'Get Your Custom Quote', pathname); navigate('/contact'); };

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="How SMCLA Compares to Cameras & Other Solutions" description="SMCLA vs generic CCTV and other platforms — side-by-side comparison of childcare abuse detection, audio AI, pattern recognition, and forensic investigation." path="/compare" />
      <Header onBookDemo={handleBookDemo} />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 14 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '30%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.1) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <SmartToyIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Why SMCLA
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2.5, lineHeight: 1.1 }}>
            The industry had cameras.<br /><Box component="span" sx={{ color: '#5ce1e6' }}>Nobody built what comes next.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 600, mx: 'auto', lineHeight: 1.65, mb: 5 }}>
            Streaming, recording, and generic security systems all exist. None of them were built specifically to detect, prevent, and document child abuse in childcare settings. SMCLA was.
          </Typography>
          <Button variant="contained" size="large" onClick={handleBookDemo} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}>
            See It in Action — Book a Demo →
          </Button>
        </Container>
      </Box>

      {/* What the market built vs what was missing */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            The Gap
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: '#f0f0f8', mb: 3, letterSpacing: '-0.02em' }}>
            What existed. What didn't.
          </Typography>
          <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.75, mb: 7, maxWidth: 600, mx: 'auto' }}>
            The childcare industry already had tools for streaming, archiving, and perimeter security. What it didn't have was a system trained to understand human behavior in a childcare context — one that could detect abuse as it happens, recognize patterns before they become crises, and build a legal record automatically.
          </Typography>
          <Grid container spacing={3}>
            {[
              { label: 'Live streaming', desc: 'Parents watch feeds. Nothing is analyzed. Nothing is flagged. Problems are only visible if someone is watching at the exact right moment.', color: '#9499b0' },
              { label: 'Recording & storage', desc: 'Footage is preserved. It confirms abuse after the fact, in court. It doesn\'t prevent it, alert anyone, or detect patterns.', color: '#9499b0' },
              { label: 'Generic security AI', desc: 'Motion detection, perimeter alerts, license plate recognition. Not built for childcare behavior. Doesn\'t understand the difference between normal interaction and abuse.', color: '#9499b0' },
              { label: 'SMCLA', desc: 'Behavioral AI built specifically for childcare. Real-time detection, audio analysis, cross-session pattern recognition, and a compliance-grade audit trail — built as one system.', color: '#5ce1e6', highlight: true },
            ].map(({ label, desc, color, highlight }) => (
              <Grid key={label} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 3, bgcolor: highlight ? 'rgba(92,225,230,0.05)' : '#13131f', border: `1px solid ${highlight ? 'rgba(92,225,230,0.25)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color, mb: 1 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>{desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* What makes SMCLA different — 8 pillars */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            What Sets Us Apart
          </Typography>
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Eight things SMCLA does<br />that the market hasn't solved.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 8, maxWidth: 560 }}>
            Each one is a capability the childcare industry was missing. Together, they form a system that does what no other platform does.
          </Typography>

          <Stack spacing={3}>
            {pillars.map(({ icon, accent, title, body }) => (
              <Box key={title} sx={{ display: 'flex', gap: 3, p: { xs: 2.5, md: 3.5 }, bgcolor: '#13131f', border: `1px solid rgba(255,255,255,0.06)`, borderLeft: `3px solid ${accent}`, borderRadius: 3, alignItems: 'flex-start' }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: `${accent}12`, border: `1px solid ${accent}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent, flexShrink: 0, mt: 0.25 }}>
                  {icon}
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: 15, md: 16 }, color: '#f0f0f8', mb: 1, lineHeight: 1.3 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Emotional abuse callout */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3, bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="md">
          <Box sx={{ p: { xs: 3, md: 5 }, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.25)', borderRadius: 3 }}>
            <Typography variant="overline" sx={{ display: 'block', color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
              The Hidden Abuse
            </Typography>
            <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
              Emotional abuse leaves no bruises.<br />SMCLA is the only platform that hears it.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 14, md: 15 }, color: '#9499b0', lineHeight: 1.75, mb: 3, maxWidth: 560 }}>
              Shouting, verbal threats, humiliation, degrading language — emotional abuse causes lasting harm and is completely invisible to cameras. SMCLA's audio AI detects it in real time.
            </Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              {[
                'Raised voices and shouting outside normal range',
                'Verbal threats directed at children',
                'Repeated degrading or humiliating language',
                'Distress vocalizations from children',
                'Multi-session patterns — the same worker, the same child, the same words',
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckIcon sx={{ color: '#ff9f43', fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Stack>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.6)', fontStyle: 'italic' }}>
              Audio analysis is configured per your state's consent requirements — we handle compliance during setup.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Why cameras fail — real cases */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Real Cases
          </Typography>
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Why cameras alone aren't enough
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', mb: 6, lineHeight: 1.7, maxWidth: 560 }}>
            In every one of these cases, cameras existed. The abuse happened anyway — because no one was watching in real time, and no system was looking for patterns.
          </Typography>
          <Stack spacing={3}>
            {failureScenarios.map(({ title, body }) => (
              <Box key={title} sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid rgba(255,107,107,0.5)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8', mb: 1 }}>
                  {title}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>
                  {body}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Why AI matters */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            The Intelligence Difference
          </Typography>
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 6 }}>
            Recording captures what happened.<br />Intelligence prevents what's next.
          </Typography>
          <Grid container spacing={4}>
            {[
              { title: 'AI never gets tired', body: 'A human reviewing 8 camera feeds for 10 hours will miss things. The AI doesn\'t blink. Every frame is analyzed, every room, every moment of every shift.' },
              { title: 'AI sees patterns over time', body: 'A single rough interaction might be ambiguous. The 12th time the same worker isolates the same child? That\'s a pattern. AI catches it. Manual review doesn\'t.' },
              { title: 'AI alerts in real time', body: 'Recording documents. Intelligence acts. There\'s a fundamental difference between capturing abuse and stopping it. SMCLA is built to do the second thing.' },
              { title: 'AI protects good staff too', body: 'False accusations are common in childcare. Timestamped, tamper-evident evidence exonerates innocent workers immediately — before the accusation ruins a career.' },
            ].map(({ title, body }) => (
              <Grid key={title} size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#5ce1e6', mb: 1 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>
                    {body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Pricing
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            What does SMCLA cost?
          </Typography>
          <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', lineHeight: 1.7, mb: 8, maxWidth: 560, mx: 'auto' }}>
            Pricing is based on your camera count and facility setup. We don't publish a fixed rate — because a single-room daycare and a 20-location chain have different needs, and you shouldn't pay the same.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'inline-flex', px: 1.5, py: 0.5, bgcolor: 'rgba(92,225,230,0.1)', border: '1px solid rgba(92,225,230,0.25)', borderRadius: 10, mb: 2.5, alignSelf: 'flex-start' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Pilot Program</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1.5, lineHeight: 1.25 }}>
                  First facilities get the best rate.
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7, flex: 1 }}>
                  We're in the early phase of our rollout. Facilities that join now receive preferred entry pricing — with a direct line to the team and early access to every new feature we ship.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(140,82,255,0.2)', borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'inline-flex', px: 1.5, py: 0.5, bgcolor: 'rgba(140,82,255,0.1)', border: '1px solid rgba(140,82,255,0.25)', borderRadius: 10, mb: 2.5, alignSelf: 'flex-start' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#8c52ff', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Custom Pricing</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1.5, lineHeight: 1.25 }}>
                  Built around your facility.
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7, flex: 1 }}>
                  Tell us your camera count, number of rooms, and whether you operate multiple locations. We'll put together a plan that fits — with no surprises on your monthly bill.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.6)', mb: 4 }}>
              No long-term contracts. No setup fees. Cancel any time.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleGetQuote}
              sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 28px rgba(92,225,230,0.2)', '&:hover': { bgcolor: '#4ecfd4' } }}
            >
              Get Your Custom Quote →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Purpose-built for childcare.<br />Nothing else comes close.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 17, color: '#9499b0', lineHeight: 1.7, mb: 5 }}>
            See the behavioral AI in action with a personalized 30-minute walkthrough. No sales pressure. Just the product.
          </Typography>
          <Button variant="contained" size="large" onClick={handleBookDemo} sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 17, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 32px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo →
          </Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
